import { Box, SxProps } from '@mui/material';
import { Header } from 'common/ui/Header';
import { ATSSelectJobStep } from 'pages/CandidatesRanking/components/steps/ATSSelectJobStep';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { processCandidateBatches } from 'services/candidate/api';
import { AtsJobInput } from 'services/candidate/types';
import { splitCandidateBatchesRequestIntoChucks } from 'services/candidate/utils/splitCandidateBatchesRequestIntoChucks';
import { useLoadingOverlay } from 'services/loading/hooks';
import { Paths } from 'services/navigation/paths';
import { ToastType, useToast } from 'services/toast';
import {
  layoutContainerPaddingBottom,
  layoutContainerPaddingTop,
  layoutContainerPaddingX
} from 'services/navigation/components/PrivateRoute';

const container: SxProps = {
  pt: layoutContainerPaddingTop,
  pb: layoutContainerPaddingBottom,
  px: layoutContainerPaddingX
};

const bodyContainer: SxProps = {
  mt: 4
};

export const AnalyseRulesetAddATSJobs = () => {
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  // This page is reached by clicking "Add jobs" from a ruleset, which passes
  // `candidateBatchGroupId` via router state. Guard against a missing state
  // (direct URL navigation, refresh, or state lost in transit) so we redirect
  // back to the rulesets list instead of crashing on a null destructure.
  const { candidateBatchGroupId } = location.state ?? {};

  if (!candidateBatchGroupId) {
    return <Navigate to={Paths.ANALYSE_RULESETS} replace />;
  }

  const handleNext = async (atsJobs: AtsJobInput[]) => {
    showLoadingOverlay();

    const atsJobChucks = splitCandidateBatchesRequestIntoChucks(atsJobs);

    try {
      await Promise.all(
        atsJobChucks.map((atsJobs) =>
          processCandidateBatches({
            atsJobs,
            candidateBatchGroupId
          })
        )
      );

      navigate(`${Paths.ANALYSE_RULESETS}/${candidateBatchGroupId}`);
    } catch {
      showToast({
        title: 'Something went wrong',
        message: 'We could not add these jobs. Please try again.',
        type: ToastType.Error
      });
    } finally {
      hideLoadingOverlay();
    }
  };

  return (
    <Box sx={container}>
      <Header
        title="Choose jobs"
        items={['Select all the jobs to use for this analysis']}
        breadcrumbs={[{ name: 'Analyse', path: Paths.ANALYSE }]}
        currentBreadcrumb="Choose jobs"
        onBack={() => navigate(-1)}
      />
      <Box sx={bodyContainer}>
        <ATSSelectJobStep atsJobs={[]} onNext={handleNext} />
      </Box>
    </Box>
  );
};
